import { supabaseClient } from "@/lib/supabase";
import type { ProfileT } from "@patient-watch/types";

export interface SelectProfilesByRolesOptions {
    roles: readonly string[];
    organisationId?: string | null;
    columns?: string;
}

/**
 * Encoded commas in `user_id=in.(...)` blow Kong's 8KB request line around 150 ids
 * (414 URI too long). Keep each profiles GET under that.
 */
const PROFILE_ID_CHUNK = 100;

/**
 * Staff pickers must not filter `profiles.roles`.
 * That column is computed per user, so `.overlaps("roles", ...)` scans patients and times out on prod.
 * Resolve user ids from `users_organisations_roles`, then load those profiles by id.
 */
export async function selectUserIdsByRoles(options: {
    roles: readonly string[];
    organisationId?: string | null;
}): Promise<string[]> {
    const roles = [...options.roles];
    if (roles.length === 0) return [];

    let query = supabaseClient.from("users_organisations_roles").select("user_id").in("role", roles);

    const organisationId = options.organisationId || undefined;
    if (organisationId) {
        query = query.eq("organisation_id", organisationId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return [...new Set((data ?? []).map((row) => row.user_id).filter(Boolean))];
}

export async function selectProfilesByRoles(options: SelectProfilesByRolesOptions): Promise<ProfileT[]> {
    const userIds = await selectUserIdsByRoles(options);
    if (userIds.length === 0) return [];

    const out: ProfileT[] = [];
    for (let i = 0; i < userIds.length; i += PROFILE_ID_CHUNK) {
        const chunk = userIds.slice(i, i + PROFILE_ID_CHUNK);
        const query = options.columns
            ? supabaseClient.from("profiles").select(options.columns).in("user_id", chunk)
            : supabaseClient.from("profiles").select("*").in("user_id", chunk);
        const { data, error } = await query;
        if (error) throw error;
        out.push(...((data ?? []) as ProfileT[]));
    }
    return out;
}
